/**
 * Global frontend error reporter with diagnostic context.
 *
 * Ships to /api/log on every error. Payload is deliberately minimal —
 * redundancy with backend-captured request/headers is avoided.
 *
 * Captured:
 *  - message + stack + url
 *  - app + channel (window.error | unhandledrejection | react.errorboundary)
 *  - componentStack (React tree at crash, captured via console.error patch too)
 *  - breadcrumbs: last ~50 non-noisy events (clicks, navigations, HTTP,
 *    control/hotkey keypresses). Plain printable typing is NOT logged.
 *  - redux: full action stream + shallow state (via Redux middleware, see
 *    createErrorReporterMiddleware)
 *  - pageLoadMs, viewport, referrer
 *
 * Values are never captured — only structural info (tag, method, status, URL,
 * key name for control keys).
 */

const FLIGHT_WINDOW_MS = 10000;
const ENDPOINT = '/api/log';
const BREADCRUMB_MAX = 50;
const REDUX_ACTIONS_MAX = 40;

const SPECIAL_KEYS = new Set([
    'Enter', 'Escape', 'Tab', 'Backspace', 'Delete',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'Home', 'End', 'PageUp', 'PageDown',
]);

let installed = false;
let appName = 'unknown';
const recentSignatures = new Map();
const breadcrumbs = [];
let recentComponentStack = null;
let recentComponentStackAt = 0;

let store = null;
const reduxActions = [];

function pushBreadcrumb(type, data) {
    const clean = {};
    for (const k of Object.keys(data || {})) {
        if (data[k] != null && data[k] !== '') clean[k] = data[k];
    }
    breadcrumbs.push({ts: Date.now(), type, ...clean});
    if (breadcrumbs.length > BREADCRUMB_MAX) breadcrumbs.shift();
}

function snapshotBreadcrumbs() {
    const now = Date.now();
    return breadcrumbs.map(b => {
        const {ts, ...rest} = b;
        return {dt_ms: ts - now, ...rest};
    });
}

function describeEl(el) {
    if (!el || el.nodeType !== 1) return null;
    const out = {tag: el.tagName?.toLowerCase()};
    if (el.id) out.id = el.id;
    if (!out.id) {
        const testId = el.getAttribute?.('data-testid');
        if (testId) out.testId = testId;
    }
    const name = el.getAttribute?.('name');
    if (name) out.name = name;
    if (!out.id && !out.testId && !out.name && typeof el.className === 'string' && el.className) {
        out.cls = el.className.split(/\s+/)[0];
    }
    const text = (el.innerText || el.textContent || '').trim();
    if (text && text.length <= 40 && !out.name) out.text = text;
    return out;
}

function isInterestingKey(event) {
    const k = event.key;
    if (!k) return false;
    if (SPECIAL_KEYS.has(k)) return true;
    if (k.startsWith('F') && /^F\d{1,2}$/.test(k)) return true;
    // Hotkey combo (Ctrl+x, Cmd+s) — log the combo, not the modifier alone
    if ((event.ctrlKey || event.metaKey || event.altKey) && k.length === 1) return true;
    return false;
}

function makeSignature(message, stack) {
    const stackHead = (stack || '').split('\n').slice(0, 3).join('|');
    return (message || '') + '::' + stackHead;
}

function alreadySent(signature) {
    const now = Date.now();
    for (const [k, t] of recentSignatures) {
        if (now - t > FLIGHT_WINDOW_MS) recentSignatures.delete(k);
    }
    if (recentSignatures.has(signature)) return true;
    recentSignatures.set(signature, now);
    return false;
}

// Reducer slices that are always URL-derived and duplicate top-level fields —
// never emit them in the state snapshot.
const NOISY_REDUX_KEYS = new Set(['query']);

function isEmpty(v) {
    if (v == null) return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
}

function safeSnapshot(state, depth = 0, seen = new WeakSet()) {
    if (state == null || typeof state !== 'object') return state;
    if (seen.has(state)) return '[circular]';
    if (depth > 2) return '[...]';
    seen.add(state);
    if (Array.isArray(state)) {
        const items = state.slice(0, 20).map(v => safeSnapshot(v, depth + 1, seen)).filter(v => !isEmpty(v));
        return items;
    }
    const out = {};
    for (const k of Object.keys(state).slice(0, 30)) {
        if (depth === 0 && NOISY_REDUX_KEYS.has(k)) continue;
        const v = safeSnapshot(state[k], depth + 1, seen);
        if (!isEmpty(v)) out[k] = v;
    }
    return out;
}

function externalReferrer() {
    const r = document.referrer;
    if (!r) return undefined;
    try {
        if (new URL(r).origin === window.location.origin) return undefined;
    } catch { return undefined; }
    return r;
}

function gatherContext() {
    const pageLoadMs = typeof performance !== 'undefined' && performance.now ? Math.round(performance.now()) : null;
    const viewport = {w: window.innerWidth, h: window.innerHeight};
    const referrer = externalReferrer();

    const redux = store ? (() => {
        const actions = reduxActions.slice(-REDUX_ACTIONS_MAX).map(a => ({type: a.type, dt_ms: a.ts - Date.now()}));
        const state = safeSnapshot(store.getState());
        const out = {};
        if (actions.length) out.actions = actions;
        if (!isEmpty(state)) out.state = state;
        return isEmpty(out) ? undefined : out;
    })() : undefined;

    const componentStack = recentComponentStack && Date.now() - recentComponentStackAt < 2000
        ? recentComponentStack
        : undefined;

    return {
        app: appName,
        pageLoadMs,
        viewport,
        referrer,
        breadcrumbs: snapshotBreadcrumbs(),
        componentStack,
        redux,
    };
}

function postPayload(payload) {
    try {
        fetch(ENDPOINT, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
            body: JSON.stringify(payload),
            credentials: 'include',
            keepalive: true,
        }).catch(() => {});
    } catch {}
}

function send(level, message, stack, extra) {
    const signature = makeSignature(message, stack);
    if (alreadySent(signature)) return;

    const payload = {
        level,
        message: String(message || ''),
        stack: String(stack || ''),
        url: window.location.href,
        ...gatherContext(),
        ...extra,
    };
    // Drop undefined keys
    for (const k of Object.keys(payload)) if (payload[k] === undefined) delete payload[k];
    postPayload(payload);
}

function patchConsoleError() {
    const orig = console.error;
    console.error = function (...args) {
        try {
            for (const a of args) {
                if (typeof a === 'string' && (a.includes('The above error occurred') || /\n\s+at \w+/.test(a))) {
                    const match = a.match(/(\n\s+at [^\n]+)+/);
                    if (match) {
                        recentComponentStack = match[0].trim();
                        recentComponentStackAt = Date.now();
                    }
                }
            }
        } catch {}
        return orig.apply(console, args);
    };
}

function installBreadcrumbs() {
    window.addEventListener('click', (e) => {
        pushBreadcrumb('click', {target: describeEl(e.target)});
    }, true);

    window.addEventListener('keydown', (e) => {
        if (!isInterestingKey(e)) return;
        const modifiers = [e.ctrlKey && 'Ctrl', e.metaKey && 'Meta', e.altKey && 'Alt', e.shiftKey && 'Shift'].filter(Boolean);
        const key = modifiers.length ? `${modifiers.join('+')}+${e.key}` : e.key;
        pushBreadcrumb('key', {key, target: describeEl(e.target)});
    }, true);

    window.addEventListener('popstate', () => {
        pushBreadcrumb('nav', {url: window.location.href, via: 'popstate'});
    });

    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...args) {
        const r = origPush.apply(this, args);
        pushBreadcrumb('nav', {url: window.location.href, via: 'pushState'});
        return r;
    };
    history.replaceState = function (...args) {
        const r = origReplace.apply(this, args);
        pushBreadcrumb('nav', {url: window.location.href, via: 'replaceState'});
        return r;
    };

    if (typeof window.fetch === 'function') {
        const origFetch = window.fetch;
        window.fetch = function (input, init) {
            const started = Date.now();
            const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
            const url = typeof input === 'string' ? input : (input?.url || '');
            return origFetch.apply(this, arguments).then(
                (res) => {
                    pushBreadcrumb('http', {method, url, status: res.status, ms: Date.now() - started});
                    return res;
                },
                (err) => {
                    pushBreadcrumb('http', {method, url, status: 'error', ms: Date.now() - started, error: err?.message});
                    throw err;
                }
            );
        };
    }

    if (typeof XMLHttpRequest === 'function') {
        const origOpen = XMLHttpRequest.prototype.open;
        const origSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (method, url) {
            this.__rb = {method: method?.toUpperCase(), url, started: Date.now()};
            return origOpen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.send = function () {
            const rb = this.__rb;
            if (rb) {
                this.addEventListener('loadend', () => {
                    pushBreadcrumb('http', {method: rb.method, url: rb.url, status: this.status, ms: Date.now() - rb.started});
                });
            }
            return origSend.apply(this, arguments);
        };
    }
}

/**
 * Whether an error came from code we actually ship.
 *
 * The window error handlers fire for every script on the page, so a hiccup in
 * Cloudflare Zaraz, GTM or the Facebook pixel arrives looking exactly like our own
 * crash — and then buries the real ones. Zaraz is the awkward case: it is injected by
 * the edge and served from OUR origin (/cdn-cgi/zaraz/s.js), so an origin check alone
 * does not catch it; its `Converting circular structure to JSON` fires on every order
 * page and we neither load nor feed it.
 *
 * An empty URL means an opaque cross-origin script ("Script error."): nothing
 * actionable is available, and our own bundles are same-origin, so it is never ours.
 */
function isOwnCode(sourceUrl) {
    if (!sourceUrl) return false;

    try {
        const {origin, pathname} = new URL(sourceUrl, window.location.href);

        if (origin !== window.location.origin) return false;

        return !pathname.startsWith('/cdn-cgi/');
    } catch (e) {
        return false;
    }
}

// First URL in a stack trace — the frame that actually threw. Extension schemes are
// matched too, so a content script injected into the page is attributed correctly.
function stackSource(stack) {
    const match = /((?:https?|chrome-extension|moz-extension|safari-web-extension):\/\/[^\s)]+)/.exec(stack || '');

    return match ? match[1] : '';
}

/**
 * Errors thrown by a browser extension or the browser's own injected tooling.
 *
 * These arrive with an EMPTY stack, so there is nothing to attribute by URL, and the
 * rejection handler keeps anything it cannot attribute — which is right for our own
 * failures but lets this class through. Each signature below belongs to an API we
 * never call (the WebExtension runtime) or to a browser-internal loop, so a match is
 * conclusive rather than a guess. Keep the list narrow for exactly that reason.
 */
const EXTENSION_NOISE = [
    'runtime.sendMessage',          // WebExtension messaging — seen from DuckDuckGo/iOS
    'Extension context invalidated',
    'chrome-extension://',
    'moz-extension://',
    'safari-web-extension://',
    'ResizeObserver loop',          // browser-internal, never actionable
];

function isExtensionNoise(message) {
    if (!message) return false;

    return EXTENSION_NOISE.some(signature => message.includes(signature));
}

export default function installGlobalErrorReporter(name = 'unknown') {
    if (installed) return;
    installed = true;
    appName = name;

    window.addEventListener('error', (event) => {
        // event.filename names the script that threw, so attribution is exact here.
        if (!isOwnCode(event?.filename)) return;

        const err = event?.error;
        if (isExtensionNoise(err?.message || event?.message)) return;

        send('error', err?.message || event?.message, err?.stack, {
            channel: 'window.error',
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event?.reason;
        // A rejection carries no filename, and plenty of ours (a failed fetch) have no
        // stack either — so drop only on positive evidence that a third party threw,
        // and keep anything we cannot attribute.
        const source = stackSource(reason?.stack);

        if (source && !isOwnCode(source)) return;

        const message = reason?.message || (typeof reason === 'string' ? reason : 'Unhandled promise rejection');

        if (isExtensionNoise(message)) return;
        send('error', message, reason?.stack, {channel: 'window.unhandledrejection'});
    });

    patchConsoleError();
    installBreadcrumbs();
}

export function sendReactRenderError({message, stack, componentStack}) {
    send('error', message, stack, {componentStack, channel: 'react.errorboundary'});
}

/**
 * Redux middleware: logs every action (including sub-dispatches from
 * redux-api-middleware, redux-thunk) and exposes store for state snapshots.
 * Install once in configureStore — no per-app wiring needed.
 */
export function createErrorReporterMiddleware() {
    return (api) => {
        store = api;
        return (next) => (action) => {
            try {
                if (action && typeof action === 'object' && action.type) {
                    reduxActions.push({type: String(action.type), ts: Date.now()});
                    if (reduxActions.length > REDUX_ACTIONS_MAX * 2) reduxActions.splice(0, reduxActions.length - REDUX_ACTIONS_MAX);
                }
            } catch {}
            return next(action);
        };
    };
}
